// Shared data for the Cutting Edge Technologies marketing site
import {
  BrainCircuit,
  GraduationCap,
  ServerCog,
  Users,
} from "lucide-react";

export const CONTACT = {
  phone: "+1 (202) 538-0104",
  phoneHref: "tel:+12025380104",
  email: "Info@cetcusa.com",
  emailHref: "mailto:Info@cetcusa.com",
  address: "8700 Commerce Park Drive, Suite 238, Houston, Texas 77036",
  mapQuery: "8700+Commerce+Park+Drive,+Suite+238,+Houston,+Texas+77036",
};

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/employers", label: "Employers" },
  { to: "/job-seekers", label: "Job Seekers" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const SERVICES = [
  {
    slug: "consulting",
    code: "01",
    icon: BrainCircuit,
    title: "IT Consulting",
    tag: "Strategy · AI · Cloud · DevOps · Data · Project Management · Cybersecurity",
    description:
      "Advisory for enterprises and growing teams. We translate business goals into pragmatic technology roadmaps across AI, cloud, data, and modernization.",
    benefits: [
      "Enterprise technology roadmaps",
      "AI & data strategy",
      "Cloud migration & modernization",
      "DevOps & platform engineering",
      "Project Management",
    ],
  },
  {
    slug: "training",
    code: "02",
    icon: GraduationCap,
    title: "Training & Certification",
    tag: "Bootcamps · Enterprise Upskilling",
    description:
      "Career-grade training programs and enterprise upskilling tracks. Instructor-led, remote-ready, and mapped to globally recognized certifications.",
    benefits: [
      "Artificial Intelligence (AI) & Machine Learning",
      "Cloud, DevOps, Cyber, Data tracks",
      "AWS / Azure / GCP certification prep",
      "Live cohorts + project mentorship",
      "Team-wide upskilling programs",
      "Project Management",
    ],
  },
  {
    slug: "managed",
    code: "03",
    icon: ServerCog,
    title: "IT Management",
    tag: "Managed Services · 24/7 Ops",
    description:
      "End-to-end managed services for your stack. We run, monitor, and optimize infrastructure so your team can focus on shipping business outcomes.",
    benefits: [
      "24/7 infrastructure monitoring",
      "Security & compliance (SOC 2, HIPAA)",
      "Backup, DR and FinOps",
      "Predictable monthly engagement",
    ],
  },
  {
    slug: "staffing",
    code: "04",
    icon: Users,
    title: "Recruiting & Staffing",
    tag: "Contract · Contract-to-Hire · Direct",
    description:
      "Access a vetted bench of engineers, architects, and tech leaders. We match talent to your roadmap — not resumes to job descriptions.",
    benefits: [
      "Pre-vetted tech talent bench",
      "Contract & direct placement",
      "Diversity-forward sourcing",
      "Rapid ramp-up (7–14 days)",
    ],
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Cutting Edge Technologies rebuilt our cloud foundation and helped us cut infrastructure spend by 32% — all while shipping faster than before.",
    name: "Priya Natarajan",
    role: "VP Engineering",
    company: "Northwind Health Systems",
  },
  {
    quote:
      "Their consultants feel like part of our team. Strategy on Monday, implementation by Friday. That's rare.",
    name: "Marcus Declan",
    role: "Chief Technology Officer",
    company: "Atlens Logistics Group",
  },
  {
    quote:
      "I went from a non-tech background to a cloud engineer role in under six months. The placement support was real — not a marketing line.",
    name: "Jasmine Okoro",
    role: "Cloud Engineer",
    company: "Placed at a Fortune 500 Bank",
  },
  {
    quote:
      "We hired three senior DevOps engineers through Cutting Edge Technologies in under two weeks. The quality of the shortlist was outstanding.",
    name: "David Osborne",
    role: "Head of Talent",
    company: "Flutterwave Fintech",
  },
  {
    quote:
      "I never imagined I'd become a Platform Engineer so quickly. The curriculum covered the latest cloud and automation technologies, and every project felt like real industry experience. Within five months, I secured a full-time role thanks to the outstanding mentorship and interview preparation.",
    name: "Daniel Brooks",
    role: "Platform Engineer",
    company: "Placed at a Leading AI Startup",
  },
  {
    quote:
      "I transitioned into an AI Engineer role in less than five months, despite having no prior tech experience. The hands-on training with cutting-edge AI tools and the career support gave me the confidence and skills employers were looking for. Landing my first AI job happened much faster than I ever expected.",
    name: "Michael Carter",
    role: "AI Engineer",
    company: "Placed at a Leading AI Startup",
  },
  {
    quote:
      "The managed services team runs our production like a Swiss watch. Incidents down 71% year-over-year.",
    name: "Elena Vasquez",
    role: "Director of Operations",
    company: "Stellar Retail Co.",
  },
];

export const FAQS = [
  {
    q: "What industries do you serve?",
    a: "We work across healthcare, financial services, logistics, retail, SaaS, public sector, and energy. Our frameworks are industry-agnostic, but our delivery leads carry deep vertical expertise so engagements start fast.",
  },
  {
    q: "Do you offer remote training?",
    a: "Yes. All of our cohorts run live online with recorded sessions, hands-on cloud labs, and 1:1 mentorship. On-site and hybrid options are available for enterprise teams of 8 or more.",
  },
  {
    q: "How does IT management (managed services) work?",
    a: "We assess your current stack, agree on SLAs and outcomes, and take over day-to-day operations — monitoring, patching, security, backups, cost optimization, and incident response. You get a dedicated engagement lead and a 24/7 on-call team.",
  },
  {
    q: "How do I get started?",
    a: "Book a free 30-minute consultation from any page. We'll listen to your goals, share comparable work, and scope next steps within 48 hours.",
  },
  {
    q: "Can you scale with us as we grow?",
    a: "Absolutely. A majority of our client engagements start with a single project and expand into multi-year partnerships spanning consulting, staffing, and managed services.",
  },
  {
    q: "Do you help with AI and modern data platforms?",
    a: "Yes — our practice covers everything from LLM-powered product workflows to Snowflake / Databricks platforms, MLOps, and responsible AI governance.",
  },
];

export const STATS = [
  { value: "120+", label: "Enterprise engagements" },
  { value: "2,400+", label: "Professionals trained" },
  { value: "98%", label: "Client retention" },
  { value: "14 days", label: "Avg. talent placement" },
];

export const TECH_MARQUEE = [
  "AWS", "AZURE", "GCP", "KUBERNETES", "TERRAFORM", "SNOWFLAKE",
  "DATABRICKS", "PYTHON", "REACT", "NODE.JS", "DOCKER", "POSTGRES",
  "MONGODB", "KAFKA", "GITHUB ACTIONS", "OPENAI", "NVIDIA", "SOC 2",
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1714601344981-75e003bc5d18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwc2t5fGVufDB8fHx8MTc3NzY4NDM5OHww&ixlib=rb-4.1.0&q=85",
  aboutTeam: "https://images.unsplash.com/photo-1758518727820-28491c194bee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBjb2xsYWJvcmF0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3NzY4NDM4NXww&ixlib=rb-4.1.0&q=85",
  servicesTech: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhdGElMjBuZXR3b3JrJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3Nzc2ODQzOTh8MA&ixlib=rb-4.1.0&q=85",
  employers: "https://images.unsplash.com/photo-1758691737278-3af15b37af48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBjb2xsYWJvcmF0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3NzY4NDM4NXww&ixlib=rb-4.1.0&q=85",
  serverRoom: "https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxzZXJ2ZXIlMjByb29tJTIwY2xvdWQlMjB0ZWNobm9sb2d5JTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc3Njg0Mzg1fDA&ixlib=rb-4.1.0&q=85",
};
